<template>
  <b-row>
    <b-col
      :lg="computedGrid.lg.cols" :offset-lg="computedGrid.lg.offset"
      :md="computedGrid.md.cols" :offset-md="computedGrid.md.offset"
      :sm="computedGrid.sm.cols" :offset-sm="computedGrid.sm.offset"
    >
      <vue-form-generator
        :model="model"
        :schema="schema"
        :options="options"
        ref="addressForm"
      />
    </b-col>
  </b-row>
</template>

<script>
import helper from '@/libs/helper';
import VueFormGenerator from 'vue-form-generator';

import ADDRESS_MODEL from '@/components/utils/address/resources/model.json';
import US_STATES from '@/components/utils/address/resources/states.json';


export default {
  /* global google:true */
  /* eslint no-undef: "error" */
  components: {
    'vue-form-generator': VueFormGenerator.component,
  },
  props: {
    grid: Object,
    form: {
      type: Object,
      default() {
        return helper.copy(ADDRESS_MODEL);
      },
    },
    placeholder: String,
    disabled: Boolean,
    removeStates: {
      type: Array,
      default() { return []; },
    },
    showResidenceStatus: {
      type: Boolean,
      default() { return false; },
    },
    removedFields: {
      type: Array,
      default() { return []; },
    },
  },
  data: () => ({
    input: null,
    model: helper.copy(ADDRESS_MODEL),
    blocks: {
      text: /[^A-Z0-9#/,.-\s]/gi,
      city: /[^A-Z0-9-\s]/gi,
      zip: /[^0-9-]/gi,
    },
    options: {
      validationErrorClass: 'invalid-input',
      validateAfterChanged: true,
      fieldIdPrefix: 'address-',
    },
  }),
  watch: {
    model: {
      deep: true,
      handler(value) {
        this.$emit('address-updated', value);
      },
    },
    isGoogleAPIEnabled() {
      this.loadGoogleAutocomplete();
    },
  },
  computed: {
    isGoogleAPIEnabled() {
      return this.$store.getters.loadedGoogleAPI;
    },
    addressPlaceholder() {
      if (!this.placeholder) {
        return this.isGoogleAPIEnabled ?
          'Start typing and we will help you with some options.' :
          'Address';
      }

      return this.placeholder;
    },
    schema() {
      const removeStates = this.removeStates;
      const addressSchema = {
        fields: [
          {
            type: 'regex',
            label: 'Address',
            placeholder: this.addressPlaceholder,
            model: 'address',
            maxlength: 100,
            required: true,
            styleClasses: 'col-md-9',
            validator: 'string',
            block: this.blocks.text,
            disabled: this.disabled,
          },
          {
            type: 'regex',
            label: 'Unit',
            model: 'unit',
            maxlength: 10,
            placeholder: 'Unit',
            styleClasses: 'col-md-3',
            block: this.blocks.text,
            disabled: this.disabled,
          },
          {
            type: 'regex',
            label: 'City',
            placeholder: 'City Name',
            model: 'city',
            required: true,
            maxlength: 50,
            styleClasses: 'col-md-6',
            validator: 'string',
            block: this.blocks.city,
            disabled: this.disabled,
          },
          {
            type: 'select',
            label: 'State',
            model: 'state',
            min: 2,
            max: 2,
            values: US_STATES.filter(state => !removeStates.includes(state.id)),
            selectOptions: {
              noneSelectedText: '-',
            },
            styleClasses: 'col-md-3',
            required: true,
            validator: 'string',
            disabled: this.disabled,
          },
          {
            type: 'regex',
            label: 'Zip Code',
            placeholder: 'Zip Code',
            model: 'zipCode',
            maxlength: 10,
            required: true,
            styleClasses: 'col-md-3',
            pattern: /^(\d{5}(-\d{4})?)$/g,
            block: this.blocks.zip,
            validator: 'regexp',
            disabled: this.disabled,
          },
        ],
      };
      // eslint-disable-next-line no-prototype-builtins
      if (this.showResidenceStatus) {
        addressSchema.fields.push(
          {
            type: 'radios',
            label: 'Do you rent or own your primary residence?',
            model: 'residenceStatus',
            required: true,
            styleClasses: 'col-md-12 bool-radio',
            values: [
              { name: 'Rent', value: 'rent' },
              { name: 'Own', value: 'own' },
            ],
            validator: VueFormGenerator.validators.required,
          });
      }
      if (this.removedFields.length > 0) {
        addressSchema.fields = addressSchema.fields.filter(
          item => !this.removedFields.includes(item.model),
        );
      }
      return addressSchema;
    },
    computedGrid() {
      const defaultGrid = { cols: 12, offset: 0 };
      const gridKeys = ['lg', 'md', 'sm'];
      let compGrid = this.grid;

      if (!compGrid) {
        compGrid = {};
      }

      gridKeys.forEach((key) => {
        if (!compGrid[key]) {
          compGrid[key] = defaultGrid;
        }
      });

      return compGrid;
    },
  },
  methods: {
    loadGoogleAutocomplete() {
      if (this.isGoogleAPIEnabled) {
        this.setAutocomplete();
      } else {
        this.unsetAutocomplete();
      }
    },
    unsetAutocomplete() {
      try {
        google.maps.event.clearInstanceListeners(this.input);
      } catch (_) { /* Ignore if global google object has not been initialized */ }

      if (this.input) {
        this.input.classList.remove('gm-err-autocomplete');
        this.input.removeAttribute('disabled');
        this.input.removeAttribute('style');
        this.input.setAttribute('placeholder', this.addressPlaceholder);
      }
    },
    setAutocomplete() {
      if (!this.input) { return; }
      const options = {
        componentRestrictions: { country: 'us' },
      };
      const autocomplete = new google.maps.places.Autocomplete(this.input, options);
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!(place && place.address_components)) { return; }
        const addressFound = place.address_components;

        function resolveFieldValue(name, longName) {
          const fieldSearch = addressFound.filter(e => e.types[0] === name);
          if (fieldSearch.length) {
            /* eslint no-param-reassign: ["error", { "props": false }] */
            return longName ? fieldSearch[0].long_name : fieldSearch[0].short_name;
          }
          return '';
        }

        const addressNumber = resolveFieldValue('street_number', true);
        const addressRoute = resolveFieldValue('route', false);
        if (addressNumber !== '' && addressRoute !== '') {
          this.model = {
            address: `${addressNumber} ${addressRoute}`.trim(),
            city: resolveFieldValue('locality', true),
            state: resolveFieldValue('administrative_area_level_1', false),
            zipCode: resolveFieldValue('postal_code', true),
            unit: null,
          };
          // validate the form after set variables due the bug in VFG for select field,
          // it doesn't fire validation on variable changes
          this.validate();
        }
      });
    },
    validate() {
      return this.$refs.addressForm.validate();
    },
    clearValidationErrors() {
      return this.$refs.addressForm.clearValidationErrors();
    },
  },
  mounted() {
    if (Object.keys(this.form).some(key => this.form[key] !== null)) {
      this.model = this.form;
    }

    const addressForm = this.$refs.addressForm;
    if ((addressForm && addressForm.$children.length)) {
      this.input = addressForm.$children[0].$el.getElementsByTagName('input')[0];
    }

    this.loadGoogleAutocomplete();
  },
};
</script>



// WEBPACK FOOTER //
// src/components/utils/address/AddressFormFields.vue